import { NavigationTypes, NavigationScreenLinkTypes, NativeResponseType } from "@swiggy-private/native-bridge";
import { STATUS_CODES } from "@swiggy-private/web-bridge-sdk";
import { isEmpty } from "@swiggy-private/js-utils";
import { safeJsonParse } from "@swiggy-private/core-ui";
import { isIxigoFlow } from "./Common";
import { getBridgeSdk } from "@checkout-core/utils/BridgeSdk";

interface NavigationResponse {
    statusCode: number;
    type: NativeResponseType;
    requestId: string;
}

const CART_FROM_LOGIN_FLOW_KEY = "cart_from_login_flow";
const WEB_FOOD_CART_LOCAL_KEY = "web_food_cart_local";

/**
 * Handles navigation to menu page when user is coming from login flow.
 * Returns navigation response if navigation succeeds, error response for Ixigo flow on failure,
 * or null for non-Ixigo flow to allow fallback to default behavior.
 */
export const handleLoginFlowNavigation = async (): Promise<NavigationResponse | null> => {
    const isSessionStorageAvailable = typeof window !== "undefined" && typeof sessionStorage !== "undefined";
    const cameFromLoginFlow = isSessionStorageAvailable && sessionStorage.getItem(CART_FROM_LOGIN_FLOW_KEY) === "true";

    if (!cameFromLoginFlow) {
        return null;
    }

    const isIxigo = isIxigoFlow();

    // Helper function: return error response for Ixigo flow, null for others (to fall through)
    const getErrorResponseIfNeeded = (): NavigationResponse | null => {
        return isIxigo ? { statusCode: STATUS_CODES.ERROR, type: NativeResponseType.FAILURE, requestId: "" } : null;
    };

    try {
        // sessionStorage is available here because cameFromLoginFlow is true
        sessionStorage.removeItem(CART_FROM_LOGIN_FLOW_KEY);

        const isLocalStorageAvailable = typeof window !== "undefined" && typeof localStorage !== "undefined";
        if (!isLocalStorageAvailable) {
            return getErrorResponseIfNeeded();
        }

        try {
            const cartData = localStorage.getItem(WEB_FOOD_CART_LOCAL_KEY);

            if (!isEmpty(cartData)) {
                const cart = safeJsonParse(cartData, {}) as { restaurantId?: string | number };
                const restaurantId = cart?.restaurantId;

                if (!isEmpty(restaurantId)) {
                    await getBridgeSdk().navigateToScreen({
                        type: NavigationTypes.SCREEN,
                        link: NavigationScreenLinkTypes.MENU,
                        params: { shouldCloseWebview: true, id: restaurantId },
                    });

                    return { statusCode: 0, type: NativeResponseType.SUCCESS, requestId: "" };
                }
            }

            // Navigation failed
            return getErrorResponseIfNeeded();
        } catch {
            // Navigation error
            return getErrorResponseIfNeeded();
        }
    } catch {
        // Outer catch
        return getErrorResponseIfNeeded();
    }
};
